import { useId } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Tooltip } from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { BALANCE_AWAITING_VOLTAGE_HATCH } from '~/constants/chartPalette'

// 低圧未着 (spec `doji-bg.md` AC-BDB-041) の印。 見た目と文言の正は design 辞書
// `ui-feasibility/screens/balance-demand-detail.md` の「低圧未着」行 (#11269)。

/**
 * 数値テーブルの時刻のセルに付けるチップ (design `js/bg-region-data.js` の
 * `LV_PENDING_CHIP`)。 待ちの状態なので warning ではなく neutral。
 *
 * [8080 差異] design は Badge を 10px (`--font-size-xs`) に縮めるが、 DS Badge の
 * 既定 (textMd) のまま使う。 辞書 `components/badge.md` は「小さめは独自の崩れ」 と
 * しており、 縮めるには DS の recipe を打ち消す必要があるため (デザイナー確認に記載)。
 */
export const AwaitingVoltageChip = (): React.ReactElement => {
  const { t } = useTranslation()
  const hint = t('page:balanceDemand.awaitingVoltage.hint')
  return (
    <Tooltip content={hint}>
      <Box
        as="span"
        display="inline-flex"
        verticalAlign="middle"
        marginLeft="4px"
        cursor="help"
        // 状態名は説明文の先頭にあるので、 名前を説明文にしても落ちない。
        // role なしの span に aria-label は付けられない (axe aria-prohibited-attr)。
        aria-label={hint}
        role="img"
        // tooltip を focus でも開けるようにする (WCAG 2.1 SC 1.4.13、 HelpIcon と同じ)。
        tabIndex={0}
      >
        <Badge status="neutral">
          {t('page:balanceDemand.awaitingVoltage.label')}
        </Badge>
      </Box>
    </Tooltip>
  )
}

/**
 * チャートの凡例 (系列トグルの並び) の末尾に置く網掛けの見本 (design
 * `balance/bg.html` の `#lv-pending-legend`)。 トグルではない。 低圧未着のコマが
 * ある時だけ出すのは呼び出し側が決める。
 */
export const AwaitingVoltageLegend = (): React.ReactElement => {
  const { t } = useTranslation()
  const { color } = BALANCE_AWAITING_VOLTAGE_HATCH
  return (
    <Flex
      as="span"
      alignItems="center"
      gap="6px"
      marginLeft={2}
      color="Text.highEmphasis"
      fontSize="sm"
      userSelect="none"
      cursor="default"
    >
      <Box
        as="span"
        flexShrink={0}
        display="inline-block"
        width="14px"
        height="14px"
        backgroundImage={`repeating-linear-gradient(-45deg, transparent 0, transparent 3px, ${color} 3px, ${color} 4.5px)`}
        borderWidth="1px"
        borderStyle="solid"
        // チャートの斜線 (45°・間隔 6px・太さ 1.5px) を 14px の箱に写した design の値
        // (`css/balance.css` の `.lv-pending-hatch-swatch`)。
        borderColor="Border.midEmphasis"
        aria-hidden="true"
      />
      {t('page:balanceDemand.awaitingVoltage.label')}
    </Flex>
  )
}

/**
 * チャートの網掛けの斜線。 `<defs>` の中に置き、 ReferenceArea の fill から
 * `url(#patternId)` で引く (design `js/charts.js` の `lv-pending-hatch`)。
 */
export const AwaitingVoltageHatchPattern = ({
  patternId,
}: {
  patternId: string
}): React.ReactElement => {
  const { color, spacing, strokeWidth } = BALANCE_AWAITING_VOLTAGE_HATCH
  return (
    <pattern
      width={spacing}
      height={spacing}
      id={patternId}
      patternTransform="rotate(45)"
      patternUnits="userSpaceOnUse"
    >
      <line
        stroke={color}
        strokeWidth={strokeWidth}
        x1={0}
        x2={0}
        y1={0}
        y2={spacing}
      />
    </pattern>
  )
}

/**
 * 網掛けの pattern の id。 一覧はエリアごとにチャートを並べるので、 チャートごとに
 * 別の id にする (同じ id が文書に複数あると参照先がどれか定まらない)。 `useId` の
 * `:` は除く — `url(#…)` の中で `:` を含む id を解決できないブラウザがあるため。
 */
export function useAwaitingVoltageHatchId(): string {
  return `awaiting-voltage-hatch-${useId().replace(/:/g, '')}`
}
